import { useMemo, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';

import { Icon } from '@iconify/react';

import { StyledInput } from '../../../../../../components/StyledInput';
import { StyledSelect } from '../../../../../../components/StyledSelect';
import { useUserDocument } from '../../../../../../services/contexts/UserDocumentContext';
import { usePlanoByDocument } from '../../../../../../services/hooks/usePlanos';
import { NewFormatValue } from '../../../../../../utils/NewFormatValue';
import { Forma, Resultado, simular } from './calc';
import { InfoDrawer } from './InfoDrawer';
import { MAX_PARCELAS } from './rates';
import {
  Badge,
  Card,
  Cards,
  Disclaimer,
  Field,
  Hint,
  InfoNote,
  PanelBody,
  PanelHeader,
  ResultRow,
  ResultsHead,
  Tab,
  Tabs,
  Transparencia,
  Wrapper,
} from './styles';

type Tipo = 'debito' | 'credito';

interface Prazo {
  key: keyof Resultado;
  label: string;
  desc: string;
}

const PRAZOS: Prazo[] = [
  {
    key: 'd1',
    label: 'D+1',
    desc: 'Recebimento no próximo dia útil (com antecipação).',
  },
  {
    key: 'vencimento',
    label: 'No vencimento',
    desc: 'Recebimento na data de vencimento (somente MDR, sem antecipação).',
  },
];

const parcelaOptions = [
  { id: 'avista', value: 'À vista' },
  ...Array.from({ length: MAX_PARCELAS - 1 }, (_, i) => ({
    id: String(i + 2),
    value: `${i + 2}x`,
  })),
];

const debitoOptions = [{ id: 'avista', value: 'À vista' }];

export const SimuladorTaxas = () => {
  const { documentSelected } = useUserDocument();

  // plano do CNPJ selecionado; cai em ZIG30 se nao houver cadastro
  const { data: planoInfo } = usePlanoByDocument(
    documentSelected as string | undefined
  );
  const plano = planoInfo?.plan ?? 'ZIG30';

  const [tipo, setTipo] = useState<Tipo>('debito');
  const [formaCredito, setFormaCredito] = useState('avista');
  const [valorCents, setValorCents] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const valor = valorCents / 100;

  const forma: Forma = useMemo(() => {
    if (tipo === 'debito') return 'debito';
    return formaCredito === 'avista' ? 'avista' : Number(formaCredito);
  }, [tipo, formaCredito]);

  const resultado = useMemo(
    () => simular(valor, plano, forma),
    [valor, plano, forma]
  );

  const handleValor = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '');
    setValorCents(Number(digits));
  };

  return (
    <Wrapper>
      <PanelHeader>
        <div className="head-title">
          <span className="title">Simulador de taxas</span>
          <Badge>Novo</Badge>
        </div>
        <p className="head-sub">
          Simule suas vendas com a Zignet e veja quanto você pode receber na
          hora.
        </p>
      </PanelHeader>

      <PanelBody>
        <Cards>
          <Card>
            <Tabs>
              <Tab
                type="button"
                $active={tipo === 'debito'}
                onClick={() => setTipo('debito')}
              >
                Débito
              </Tab>
              <Tab
                type="button"
                $active={tipo === 'credito'}
                onClick={() => setTipo('credito')}
              >
                Crédito
              </Tab>
            </Tabs>

            <Field>
              <label htmlFor="simulador-valor">Digite o valor da venda</label>
              <StyledInput
                id="simulador-valor"
                inputMode="numeric"
                placeholder="R$ 0,00"
                value={valorCents ? NewFormatValue(valor) : ''}
                onChange={handleValor}
              />
              <Hint>Informe o valor que será inserido na maquininha.</Hint>
            </Field>

            <Field>
              <label htmlFor="simulador-forma">Forma de pagamento</label>
              <StyledSelect
                options={tipo === 'debito' ? debitoOptions : parcelaOptions}
                value={tipo === 'debito' ? 'avista' : formaCredito}
                setValue={setFormaCredito}
                disabled={tipo === 'debito'}
              />
            </Field>

            <InfoNote>
              <Icon icon="mdi:information-outline" />
              As taxas podem variar de acordo com o seu negócio e o plano
              contratado.
            </InfoNote>
          </Card>

          <Card>
            <ResultsHead>
              <span />
              <span>
                Você absorve a taxa
                <small>você recebe</small>
              </span>
              <span>
                Você repassa ao cliente
                <small>o cliente paga</small>
              </span>
            </ResultsHead>

            {PRAZOS.map((prazo) => (
              <ResultRow key={prazo.key}>
                <div className="info">
                  <span className="label">
                    {prazo.label}
                    <span
                      title={prazo.desc}
                      style={{ display: 'inline-flex' }}
                    >
                      <Icon icon="mdi:help-circle-outline" />
                    </span>
                  </span>
                  <span className="desc">{prazo.desc}</span>
                </div>
                <strong>{NewFormatValue(resultado[prazo.key].absorve)}</strong>
                <strong className="repassa">
                  {NewFormatValue(resultado[prazo.key].repassa)}
                </strong>
              </ResultRow>
            ))}

            <Disclaimer>
              <Icon icon="mdi:shield-check-outline" />
              Valores simulados sobre o total da venda, conforme o plano
              contratado.
            </Disclaimer>
          </Card>
        </Cards>

        <Transparencia>
          <div className="icon">
            <Icon icon="mdi:percent-outline" />
          </div>
          <div className="texts">
            <h4>Transparência nas taxas</h4>
            <p>
              As taxas apresentadas são simuladas e podem variar conforme seu
              plano, segmento e volume de vendas.
            </p>
          </div>
          <button
            type="button"
            className="saibaMais"
            onClick={() => setDrawerOpen(true)}
          >
            Saiba mais sobre taxas
            <FiChevronRight />
          </button>
        </Transparencia>
      </PanelBody>

      <InfoDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </Wrapper>
  );
};
